import { MaterialTopTabBarProps } from '@react-navigation/material-top-tabs';
import React, { useEffect, useRef, useState } from 'react';
import {
  Animated,
  Dimensions,
  LayoutChangeEvent,
  StyleProp,
  StyleSheet,
  TouchableOpacity,
  ViewStyle,
} from 'react-native';

import { AvatarProps } from '@components/Avatar/Avatar';
import MemberListSummary from '@components/MemberListSummary/MemberListSummary';
import { Box, ShadowBox, Text } from '@components/Restyle/index';
import Icon from '@components/shared/Icon/Icon';
import { ProjectMember } from '@graphql/generated';
import { useHover } from '@hooks/useHover';
import documentIcons from '@themes/documentIcons';
import Images from '@themes/images';
import theme from '@themes/theme';

/**
 * Tab hover rule (TA-9005): hovering an inactive tab shows the active visual
 * state. Render-prop wrapper so hover state is owned by a component and not a
 * hook call inside the routes `.map()`.
 */
const HoverableTab = ({
  children,
}: {
  children: (isHovered: boolean) => React.ReactElement;
}) => {
  const { isHovered, hoverProps } = useHover();
  return <Box {...hoverProps}>{children(isHovered)}</Box>;
};

export type CustomerTabbarProps = {
  spacing?: number;
  panelKey: string;
  initialRouteName?: string;
  contentContainerStyle?: StyleProp<ViewStyle>;
  isProjectTabs?: boolean;
  isProfileTabs?: boolean;
  nonMember?: boolean;
  members?: ProjectMember[];
  navigateToProjectMembers?: () => void;
  displayWidth?: number;
};

type IProps = MaterialTopTabBarProps & CustomerTabbarProps;

const CustomTabPanel = ({
  state,
  descriptors,
  navigation,
  spacing = theme.spacing.m,
  initialRouteName,
  contentContainerStyle,
  isProjectTabs = false,
  isProfileTabs = false,
  nonMember = false,
  panelKey,
  members,
  displayWidth,
}: IProps): JSX.Element => {
  const scaleX_value = useRef(new Animated.Value(0)).current;
  const translateX_value = useRef(new Animated.Value(0)).current;

  type Position = {
    index: number;
    width: number;
    x: number;
  };

  const [positions, setPositions] = useState<Position[]>([]);

  // get the label widths on mount
  const onLayout = (event: LayoutChangeEvent, index: number, label: string) => {
    const { width, x } = event.nativeEvent.layout;

    const calcX = x + width / 2;

    setPositions((pos) => [...pos, { index: index, width: width, x: calcX }]);

    if (initialRouteName && initialRouteName === label) {
      scaleX_value.setValue(width);
      translateX_value.setValue(calcX);
    }

    if (!initialRouteName && index === 0) {
      scaleX_value.setValue(width);
      translateX_value.setValue(calcX);
    }
  };

  useEffect(() => {
    const newPos = positions.findIndex((i) => i.index === state.index);
    positions.length > 0 && animate(newPos);
  }, [state, spacing]);

  const getNewPosX = (position: Position) => {
    return position.x;
  };

  const animate = (newPos: number) => {
    Animated.parallel([
      Animated.timing(translateX_value, {
        toValue: getNewPosX(positions[newPos]),
        duration: 300,
        useNativeDriver: true,
      }),
      Animated.timing(scaleX_value, {
        toValue: positions[newPos].width,
        duration: 300,
        useNativeDriver: true,
      }),
    ]).start();
  };

  // basic labels as suggested by react navigation
  const labels = state.routes.map((route, index) => {
    const { options } = descriptors[route.key];
    const label = route.params?.tabBarLabel || options.tabBarLabel;
    const isFocused = state.index === index;

    const onPress = () => {
      const event = navigation.emit({
        type: 'tabPress',
        target: route.key,
        canPreventDefault: true,
      });

      if (!isFocused && !event.defaultPrevented) {
        // The `merge: true` option makes sure that the params inside the tab screen are preserved

        // @ts-ignore
        navigation.navigate({ name: route.name, merge: true });
      }
    };

    const routeIcons: Record<
      string,
      keyof typeof Images | keyof typeof documentIcons
    > = {
      'project-details': 'Folder',
      tasks: 'Hash',
      files: 'File',
      'activity-log': 'Zap',
      checklist: 'FileText',
      members: 'Users',
      'my-profile': 'UserCircle',
      'subscription-plans': 'Zap',
      settings: 'Settings',
      'team-plan': 'Star',
    };

    const getIcon = () => {
      return routeIcons[route.name] || 'Hash';
    };

    const userAvatars = (members: ProjectMember[]): AvatarProps[] => {
      return (
        members?.map((u) => {
          const { user } = u;
          const { name, id, avatar } = user;
          return {
            id: id,
            label: name,
            avatar: avatar,
            size: 'memberList',
          };
        }) || []
      );
    };

    let tabWidth = Dimensions.get('window').width >= 1920 ? 190 : 145;

    if (route.name === 'team-plan') {
      return (
        <Box flex={1} flexDirection='row' justifyContent='flex-end'>
          <HoverableTab key={route.key}>
            {(isHovered) => {
              // Hover = active visual state (TA-9005).
              const active = isFocused || (isHovered && !nonMember);
              const bgColor = active
                ? theme.colors.greenSecondaryMild
                : theme.colors.white;
              const borderColor = active
                ? theme.colors.greenSecondaryMild
                : theme.colors.purple;
              const bgColorIcon = active
                ? theme.colors.greenSecondary
                : theme.colors.purple;
              const bgColorText = active
                ? theme.colors.greenSecondary
                : theme.colors.textPrimary;
              return (
                <ShadowBox
                  key={route.key}
                  width={130}
                  borderWidth={1}
                  borderRadius='xs'
                  justifyContent='flex-start'
                  style={[
                    {
                      marginRight: theme.spacing.m,
                      backgroundColor: nonMember ? theme.colors.white : bgColor,
                      borderColor: nonMember
                        ? theme.colors.grey02
                        : borderColor,
                    },
                  ]}>
                  <Box
                    accessibilityLabel={label}
                    alignItems='center'
                    justifyContent='flex-start'
                    onLayout={(event) => onLayout(event, index, label)}>
                    <TouchableOpacity
                      accessibilityRole='button'
                      accessibilityState={isFocused ? { selected: true } : {}}
                      style={{
                        width: 130,
                      }}
                      accessibilityLabel={options.tabBarAccessibilityLabel}
                      onPress={onPress}
                      disabled={nonMember}>
                      <Box
                        padding='xs'
                        flexDirection='row'
                        alignItems='center'
                        justifyContent='flex-start'>
                        <Box
                          alignItems='center'
                          justifyContent='center'
                          marginRight='xs'
                          style={{
                            borderRadius: 5,
                            padding: 6,
                            backgroundColor: nonMember
                              ? theme.colors.grey03
                              : bgColorIcon,
                          }}>
                          <Icon name={getIcon()} variant='m' color='white' />
                        </Box>
                        <Box
                          flex={1}
                          flexDirection='row'
                          alignItems='center'
                          justifyContent='flex-start'>
                          <Text
                            variant='bodyTaskName'
                            textAlign='center'
                            style={{
                              color: nonMember
                                ? theme.colors.grey03
                                : bgColorText,
                            }}>
                            {label}
                          </Text>
                        </Box>
                      </Box>
                    </TouchableOpacity>
                  </Box>
                </ShadowBox>
              );
            }}
          </HoverableTab>
        </Box>
      );
    }

    if (isProjectTabs || isProfileTabs) {
      let xMembers = members;
      if (isProjectTabs && displayWidth && tabWidth === 190) {
        if (
          displayWidth <
          24 * 2 +
            tabWidth * state.routes.length +
            (state.routes.length - 1) * spacing +
            (members ? 80 : 0)
        ) {
          tabWidth = 145;
        } else if (
          displayWidth <
            24 * 2 +
              tabWidth * state.routes.length +
              (state.routes.length - 1) * spacing +
              80 &&
          xMembers
        ) {
          xMembers = undefined;
        }
      }

      let xTabWidth =
        route.name === 'members' && xMembers ? tabWidth + 80 : tabWidth;

      if (route.name === 'subscription-plans') xTabWidth = 190;
      return (
        <HoverableTab key={route.key}>
          {(isHovered) => {
            // Hover = active visual state (TA-9005).
            const active = isFocused || (isHovered && !nonMember);
            const bgColor = active
              ? theme.colors.greenSecondaryMild
              : theme.colors.white;
            const borderColor = active
              ? theme.colors.greenSecondaryMild
              : theme.colors.grey02;
            const iconColor = active
              ? theme.colors.greenSecondary
              : theme.colors.textPrimary;
            return (
              <ShadowBox
                key={route.key}
                width={xTabWidth}
                borderWidth={1}
                borderRadius='xs'
                justifyContent='flex-start'
                style={[
                  {
                    marginRight:
                      Dimensions.get('window').width <= 1280
                        ? theme.spacing.xs
                        : spacing,
                    backgroundColor: nonMember ? theme.colors.white : bgColor,
                    borderColor: nonMember ? theme.colors.grey02 : borderColor,
                  },
                ]}>
                <Box
                  key={route.key}
                  accessibilityLabel={label}
                  alignItems='center'
                  justifyContent='flex-start'
                  onLayout={(event) => onLayout(event, index, label)}>
                  <TouchableOpacity
                    accessibilityRole='button'
                    accessibilityState={isFocused ? { selected: true } : {}}
                    style={{
                      width: xTabWidth,
                    }}
                    accessibilityLabel={options.tabBarAccessibilityLabel}
                    onPress={onPress}
                    disabled={nonMember}>
                    <Box
                      padding='xs'
                      flexDirection='row'
                      alignItems='center'
                      justifyContent='flex-start'>
                      <Box
                        alignItems='center'
                        justifyContent='center'
                        marginRight='xs'
                        style={{
                          borderRadius: 5,
                          padding: 6,
                          backgroundColor: nonMember
                            ? theme.colors.grey03
                            : iconColor,
                        }}>
                        <Icon name={getIcon()} variant='m' color='white' />
                      </Box>
                      <Box
                        flex={1}
                        flexDirection='row'
                        alignItems='center'
                        justifyContent='flex-start'>
                        <Text
                          variant='bodyTaskName'
                          textAlign='center'
                          style={{
                            color: nonMember ? theme.colors.grey03 : iconColor,
                          }}>
                          {label}
                        </Text>
                      </Box>
                      {xMembers && route.name === 'members' && (
                        <MemberListSummary
                          avatars={userAvatars(xMembers)}
                          onPress={nonMember ? undefined : onPress}
                          maxAvatars={3}
                        />
                      )}
                    </Box>
                  </TouchableOpacity>
                </Box>
              </ShadowBox>
            );
          }}
        </HoverableTab>
      );
    }

    return (
      <Box
        key={route.key}
        alignItems='center'
        justifyContent='center'
        style={{ marginRight: spacing }}
        onLayout={(event) => onLayout(event, index, label)}>
        <HoverableTab>
          {(isHovered) => {
            // Hover = active visual state (TA-9005).
            const active = isFocused || isHovered;
            return (
              <TouchableOpacity
                accessibilityRole='button'
                accessibilityState={isFocused ? { selected: true } : {}}
                style={
                  active
                    ? {
                        backgroundColor: theme.colors.greenSecondaryMild,
                        width: 120,
                        borderRadius: 4,
                      }
                    : {
                        width: 120,
                      }
                }
                accessibilityLabel={options.tabBarAccessibilityLabel}
                onPress={onPress}>
                <Box paddingVertical='xs'>
                  <Text
                    variant='labelAssignedTask'
                    textAlign='center'
                    color={active ? 'greenSecondary' : 'grey04'}>
                    {label}
                  </Text>
                </Box>
              </TouchableOpacity>
            );
          }}
        </HoverableTab>
      </Box>
    );
  });

  return (
    <Box flex={1} key={panelKey}>
      <Animated.View style={{ ...styles.container, ...contentContainerStyle }}>
        {labels}
        <Animated.View
          style={[
            {
              transform: [
                { translateX: translateX_value },
                { scaleX: scaleX_value },
              ],
            },
          ]}
        />
      </Animated.View>
    </Box>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
  },
  indicator: {
    backgroundColor: theme.colors.greenSecondary,
    height: 2,
    position: 'absolute',
    bottom: 0,
    right: 0,
    left: 0,
    width: 1,
  },
});

export default CustomTabPanel;
